import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input, signal, output, viewChild } from '@angular/core';
import { DateTime } from 'luxon';
import { GenericObject } from '../../core/models/core/generic-object';
import { OpenCloseTime } from '../../core/models/open-close-time.model';
import { OrderType } from '../../core/models/order-type.enum';
import { DeferService } from '../../core/services/ordering/defer.service';
import { CustomDropdownComponent } from "../custom-dropdown/custom-dropdown.component";
import { IconNameType } from '../icons/icon-name-type.enum';
import { DateTimePickerComponent } from "../ordering/date-time-picker/date-time-picker.component";

export type ButtonTypes = 'CALENDAR' | 'TIME' | 'ORDERING_CALENDAR';

@Component({
  selector: 'cwo-iconed-selection-button',
  standalone: true,
  imports: [
    CommonModule,
    DateTimePickerComponent,
    CustomDropdownComponent
],
  templateUrl: './iconed-selection-button.component.html',
  styleUrl: './iconed-selection-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IconedSelectionButtonComponent implements OnInit {
 
  deferService = inject(DeferService);
  orderType = input<OrderType>(OrderType.Any);
  for = input.required<ButtonTypes>();
  stared = input<boolean>(true);
  disabled = input<boolean>(false);
  label = input<string>();
  _label = signal<string>('');
  inputDate = input<DateTime | undefined>();
  deferredDayTimes = input<GenericObject<OpenCloseTime | undefined> | undefined>();

  readonly defferDateChanged = output<DateTime>();
  readonly availableHours = output<string[]>();
  readonly storeClosedForSelectedDate = output<boolean>();
  readonly dropdown = viewChild.required<CustomDropdownComponent>('dropdown');

  calendarIcon = IconNameType.calendar;

  ngOnInit(): void {
    this._label.set('Date');
  }

  updateDateTimeFromTimeslots(timeSlots: string[], date: DateTime, forcUseFirstTimeSlot = false) {    
    if (timeSlots.length > 0) {
      let firstAvailableDate = DateTime.fromISO(timeSlots[0], {setZone: false});
      firstAvailableDate = firstAvailableDate.set({
        day: date.day,
        month: date.month,
        year: date.year,
        second: 0,
        millisecond: 0,
      });
      if (!forcUseFirstTimeSlot) {
        if (this.inputDate()) {
          const matchingTimeSlot = timeSlots.find(slot => {
            const slotDateTime = DateTime.fromISO(slot, { setZone: false });
            return slotDateTime.hasSame(this.inputDate()!, 'hour') && slotDateTime.hasSame(this.inputDate()!, 'minute');
          });
          if (matchingTimeSlot) {
            firstAvailableDate = DateTime.fromISO(matchingTimeSlot, { setZone: false });
          } else {
            const closestTimeSlot = timeSlots.reduce((closest, current) => {
              const currentDateTime = DateTime.fromISO(current, { setZone: false });
              return Math.abs(currentDateTime.diff(this.inputDate()!).as('minutes')) < Math.abs(DateTime.fromISO(closest, { setZone: false }).diff(this.inputDate()!).as('minutes')) ? current : closest;
            });
            firstAvailableDate = DateTime.fromISO(closestTimeSlot, { setZone: false });
          }
        }
      }
      this.defferDateChanged.emit(firstAvailableDate);
    } else {
      this.defferDateChanged.emit(date);
    }
  }

  handleDateChangeFromCalComp(date: DateTime){
    this.deferService.getTimeSlots(date.toISO()!, this.orderType()).subscribe(timeSlots => {
      this.availableHours.emit(timeSlots);
      this.updateDateTimeFromTimeslots(timeSlots, date);
    });
    this.dropdown()?.closeDropdown();
  }

  formatDate(date: DateTime | undefined): string | null{
    return date?.toFormat(this.for() === 'TIME' ? 'hh:mm a' : 'MM/dd/yyyy') ?? null;
  }
}